import { useState } from 'react';
import { api } from '../api.js';

/**
 * Canal do WhatsApp, ao lado do grupo.
 *
 * Ligado, todo post de WhatsApp da conta sai também no canal — do piloto, do
 * lote ou avulso — com status e retentativa próprios na Agenda. O canal é a
 * porta que se acha na busca do WhatsApp; o grupo continua sendo o VIP.
 */
export function WhatsappChannelPanel({
  canalNome,
  onMudou,
}: {
  canalNome: string | null;
  onMudou: () => Promise<void>;
}) {
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);

  async function acao(chave: string, fn: () => Promise<string>) {
    setBusy(chave);
    setAviso(null);
    try {
      const texto = await fn();
      setAviso({ ok: true, texto });
      await onMudou();
    } catch (cause) {
      setAviso({ ok: false, texto: cause instanceof Error ? cause.message : 'Algo deu errado' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius)',
        padding: '12px 14px',
        display: 'grid',
        gap: 10,
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <span aria-hidden="true" style={{ flex: '0 0 auto' }}>
          📢
        </span>
        <strong style={{ flex: 1 }}>Canal do WhatsApp</strong>
        <span className={`badge ${canalNome ? 'ok' : ''}`} style={{ flex: '0 0 auto' }}>
          {canalNome ? 'ligado' : 'opcional'}
        </span>
      </div>

      {canalNome ? (
        <>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Todo post que vai para o grupo sai também em <strong>{canalNome}</strong>, com a foto
            do produto e o mesmo texto. Na Agenda, o canal aparece como 📢 Canal, com status
            próprio.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{ flex: '0 0 auto' }}
              disabled={busy !== null}
              onClick={() =>
                void acao('teste', async () => {
                  await api.waChannelTest();
                  return 'Mensagem de teste enviada. Confira no canal.';
                })
              }
            >
              {busy === 'teste' ? <span className="spinner" /> : 'Enviar teste'}
            </button>
            <button
              type="button"
              className="danger"
              style={{ flex: '0 0 auto' }}
              disabled={busy !== null}
              onClick={() => {
                const ok = window.confirm(
                  'Parar de publicar no canal? O grupo continua normal, e o que ainda ia sair no ' +
                    'canal é pulado.',
                );
                if (!ok) return;
                void acao('remover', async () => {
                  const r = await api.waChannelRemove();
                  return (
                    'Canal desligado. Os próximos posts saem só no grupo' +
                    (r.pulados > 0 ? ` (${r.pulados} envio(s) ao canal pulados).` : '.')
                  );
                });
              }}
            >
              {busy === 'remover' ? <span className="spinner" /> : 'Desligar canal'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Cole o link do canal para que todo post do grupo saia também nele. O número conectado
            aqui precisa ser dono ou administrador do canal.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://whatsapp.com/channel/..."
              aria-label="Link do canal"
              autoComplete="off"
              style={{ flex: 1, minWidth: 220 }}
            />
            <button
              type="button"
              className="primary"
              style={{ flex: '0 0 auto' }}
              disabled={busy !== null || !link.trim()}
              onClick={() =>
                void acao('ligar', async () => {
                  const c = await api.waChannelSave(link.trim());
                  setLink('');
                  return (
                    `Canal “${c.name}” ligado. ` +
                    (c.agendadosNoCanal > 0
                      ? `Os ${c.agendadosNoCanal} post(s) já agendados vão sair nele também. `
                      : '') +
                    'Use “Enviar teste” para conferir.'
                  );
                })
              }
            >
              {busy === 'ligar' ? <span className="spinner" /> : 'Ligar canal'}
            </button>
          </div>
        </>
      )}

      {aviso && (
        <p
          className={aviso.ok ? 'muted' : 'err'}
          style={{ margin: 0, fontSize: 12.5, color: aviso.ok ? 'var(--gain)' : undefined }}
        >
          {aviso.texto}
        </p>
      )}
    </div>
  );
}
